import React, { useState, useEffect } from 'react';
import { useTable, usePagination, useGlobalFilter, useSortBy, useExpanded } from 'react-table';
import 'bootstrap/dist/css/bootstrap.min.css';

// 🔍 Global Filter with Search + Date + OK/X Toggle
function GlobalFilter({ globalFilter, setGlobalFilter, handleDateFilter, resetFilters }) {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [isFiltered, setIsFiltered] = useState(false);

  const applyDateFilter = () => {
    handleDateFilter(fromDate, toDate);
    setIsFiltered(true);
  };

  const clearDateFilter = () => {
    setFromDate('');
    setToDate('');
    setIsFiltered(false);
    resetFilters();
  };

  return (
    <div className="dataTable_search mb-3">
      {/* Search and Date Filters Row */}
      <div className="row g-2 align-items-end">
        {/* Search Input */}
        <div className="col-12 col-md-auto">
          <div className="position-relative">
            <input
              value={globalFilter || ''}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="form-control"
              placeholder="Search..."
              style={{ minWidth: '200px' }}
            />
            {globalFilter && (
              <button
                className="btn btn-sm btn-light position-absolute end-0 top-50 translate-middle-y border-0"
                onClick={() => setGlobalFilter('')}
                style={{ color: '#999' }}
                title="Clear search"
              >
                ✖
              </button>
            )}
          </div>
        </div>

        {/* Date Inputs - Stack on mobile, inline on desktop */}
        <div className="col-12 col-md-auto">
          <div className="row g-2">
            <div className="col-6 col-md-auto">
              <label className="form-label small mb-1 d-md-none">From Date</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="form-control"
              />
            </div>
            <div className="col-6 col-md-auto">
              <label className="form-label small mb-1 d-md-none">To Date</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="form-control"
              />
            </div>
          </div>
        </div>

        {/* Filter Buttons */}
        <div className="col-12 col-md-auto">
          <div className="d-flex gap-2">
            {!isFiltered ? (
              <button onClick={applyDateFilter} className="btn btn-primary btn-sm">
                OK
              </button>
            ) : (
              <button onClick={clearDateFilter} className="btn btn-outline-danger btn-sm" title="Clear dates">
                ❌
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// 📊 Main DataTable
export default function DataTable({ columns, data, renderRowSubComponent }) {
  const [filteredData, setFilteredData] = useState(data);

  useEffect(() => {
    setFilteredData(data);
  }, [data]);

  // 📅 Filter invoices by date range
  const handleDateFilter = (fromDate, toDate) => {
    if (fromDate || toDate) {
      const filtered = data.filter((item) => {
        const itemDates = item.invoices.map((inv) =>
          new Date(inv.date).setHours(0, 0, 0, 0)
        );
        const from = fromDate ? new Date(fromDate).setHours(0, 0, 0, 0) : null;
        const to = toDate ? new Date(toDate).setHours(0, 0, 0, 0) : null;

        return itemDates.some(
          (date) => (!from || date >= from) && (!to || date <= to)
        );
      });
      setFilteredData(filtered);
    } else {
      setFilteredData(data);
    }
  };

  // ♻️ Reset filters
  const resetFilters = () => {
    setFilteredData(data);
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    state: { pageIndex, pageSize, globalFilter },
  } = useTable(
    {
      columns,
      data: filteredData,
      initialState: { pageIndex: 0 },
    },
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination
  );

  return (
    <div className="dataTable_wrapper container-fluid">
      <GlobalFilter
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        handleDateFilter={handleDateFilter}
        resetFilters={resetFilters}
      />

      <div className="table-responsive">
        <table {...getTableProps()} className="table table-striped" style={{ fontSize: '13px' }}>
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()} className="dataTable_headerRow">
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    className="dataTable_headerCell"
                  >
                    {column.render('Header')}
                    <span>
                      {column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()} className="dataTable_body">
            {page.map((row) => {
              prepareRow(row);
              return (
                <React.Fragment key={row.id}>
                  <tr {...row.getRowProps()} className="dataTable_row">
                    {row.cells.map((cell) => (
                      <td {...cell.getCellProps()} className="dataTable_cell">
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                  {row.isExpanded && (
                    <tr>
                      <td colSpan={columns.length}>{renderRowSubComponent({ row })}</td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* 🔢 Pagination - Improved Mobile Responsiveness */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3 mt-3">
        {/* Page Info */}
        <div className="text-center text-md-start">
          Page <strong>{pageIndex + 1}</strong> of <strong>{pageOptions.length}</strong>
        </div>
        
        {/* Pagination Buttons */}
        <div className="d-flex gap-2">
          <button
            className="btn btn-primary btn-sm"
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
          >
            Prev
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => nextPage()}
            disabled={!canNextPage}
          >
            Next
          </button>
        </div>
        
        {/* Page Size Selector */}
        <div className="d-flex align-items-center gap-2">
          <span className="d-none d-sm-inline">Show:</span>
          <select
            className="form-select form-select-sm"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            style={{ width: 'auto' }}
          >
            {[5, 10, 20].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}